import React, { ReactElement, useEffect, useState } from 'react';
import {
  Col,
  Link,
  Loader,
  Row,
  Text,
  usePageNumberFromUrl,
  usePerPageValueFromUrl,
  useSortValueFromUrl,
} from 'idsk-react-components';
import {
  MobileFilterModal, Pagination, useResponsiveContext,
} from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import PrispevokVestnikaListFilter from '../../model/PrispevokVestnikaListFilter';
import getPaginationTotalLabel from '../../function/getPaginationTotalLabel';
import countActiveFilters from '../../function/countActiveFilters';
import FilterTagsComponent from './component/FilterTagsComponent';
import SidePanel from './component/SidePanel';
import usePrispevokVestnikaFilterList from './hooks/usePrispevokVestnikaFilterList';
import { SafeParameterSetter } from '../../model/types/SafeParameterSetter';
import getUpdatedSearchParams from './function/getUpdatedSearchParams';
import PrispevokVestnikaListComponent from './component/PrispevokVestnikaListComponent';
import { defaultPerPageValue, perPageDataArray } from '../../constants/PaginationPerPageConstants';
import UserRole from '../../model/currentuser/UserRole';
import routes from '../../routing/routes';
import Secured from '../Secured';
import './prispevokVestnikaList.scss';
import emptyFilter from './emptyFilter';
import SearchInput from './component/SearchInput';
import MobileFilters from './component/MobileFilters';
import PrispevokVestnikaListSort from './component/PrispevokVestnikaListSort';

// dvojice od/do sa pouzivatelovi zobrazuju ako jeden panel a jeden filter tag,
// takze kazda z nich je jedno kriterium
const criteriaGroups: Array<Array<keyof PrispevokVestnikaListFilter>> = [
  ['datumUcinnostiOd', 'datumUcinnostiDo'],
  ['datumZverejneniaOd', 'datumZverejneniaDo'],
];

interface Props {
  initialFilter: PrispevokVestnikaListFilter;
}

const PrispevokVestnikaList = ({
  initialFilter,
}: Props): ReactElement => {
  const { page } = usePageNumberFromUrl('page');
  const [firstLoad, setFirstLoad] = useState(true);
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort } = useSortValueFromUrl();
  const [filter, setFilter] = useState<PrispevokVestnikaListFilter>(initialFilter);
  const [filterCopy, setFilterCopy] = useState<PrispevokVestnikaListFilter>(filter);
  const { prispevokVestnikaPage, requestStatus } = usePrispevokVestnikaFilterList(filter);
  const { isSmallScreen } = useResponsiveContext();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onUpdateField: SafeParameterSetter<PrispevokVestnikaListFilter> = (fieldName, value): void => {
    setFilter({
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<PrispevokVestnikaListFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    setSearchParams(updatedSearchParams.toString());
  };

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      setSearchParams(urlParamsFromFilter);
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter, criteriaGroups) >= 2 ? clearFilters : null;

  return (
    <div className="prispevok-vestnika-list">
      <Secured allowForRoles={[UserRole.REZORTNE_ZBIERKY_SPRAVCA, UserRole.REZORTNE_ZBIERKY_IMPORT]}>
        <Link
          className="prispevok-vestnik-add-button"
          type="button-action"
          size="large"
          to={routes.prispevokVestnika.add.to}
        >
          + Nový príspevok vestníka
        </Link>
      </Secured>
      {isSmallScreen && (
      <SearchInput
        filterCopy={filterCopy}
        label="Hľadaný výraz"
        filterSearchText={filterCopy?.searchTextVyraz}
        fieldName="searchTextVyraz"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper"
        maxLength={764}
      />
      )}
      <MobileFilterModal
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        isOpen={isOpenModal}
        numberOfResults={prispevokVestnikaPage?.totalElements || 0}
        filters={(
          <MobileFilters
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={onClearFilters}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
      />
      {!isOpenModal && (
        <Row className="prispevok-vestnika-list-component__results-row">
          {!isSmallScreen && (
            <Col className="side-panel-column" width="one-quarter">
              <SidePanel
                setFilter={setFilter}
                filterCopy={filterCopy}
                onUpdateFieldCopy={onUpdateFieldCopy}
                filter={filter}
                onUpdateField={onUpdateField}
              />
            </Col>
          )}
          <Col width="three-quarters">
            <FilterTagsComponent
              filter={filter}
              onUpdateField={onUpdateField}
              onClearFilters={onClearFilters}
            />
            <Loader loading={requestStatus.loading}>
              <>
                {isSmallScreen && (
                <Row className="prispevok-vestnika-mobile-sort-select-row">
                  <Col>
                    <PrispevokVestnikaListSort />
                  </Col>
                </Row>
                )}
                <Row className="pagination-label-sort-row">
                  <Col
                    width="full"
                    widthDesktop="one-third"
                    className={isSmallScreen && 'pagination-label-mobile'}
                  >
                    <Text className="pagination-total-label">
                      {getPaginationTotalLabel(prispevokVestnikaPage, page)}
                    </Text>
                  </Col>
                  {!isSmallScreen && (
                  <Col width="full" widthDesktop="two-thirds">
                    <PrispevokVestnikaListSort />
                  </Col>
                  )}
                </Row>
                {prispevokVestnikaPage && (
                  <div className="prispevok-vestnika-list-component">
                    <PrispevokVestnikaListComponent
                      isSelectedFilter={JSON.stringify(filter) !== JSON.stringify(emptyFilter)}
                      prispevokVestnikaPage={prispevokVestnikaPage}
                    />
                    <Row className="prispevok-vestnika-list-pagination-row">
                      <Col>
                        <Pagination
                          recordsPerPage={perPageDataArray}
                          hideTotal
                          number={page}
                          size={prispevokVestnikaPage.size}
                          totalElements={prispevokVestnikaPage.totalElements}
                          perPageValue={perPageValue}
                          onRecordsPerPageChange={onRecordsPerPageChange}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </>
            </Loader>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PrispevokVestnikaList;
